/**
 * SettingsNavScrollArea — przewijana lista nawigacji Ustawień bez widocznego suwaka, ze
 * strzałkami nad i pod listą (zachowanie jak kolorowy pasek obszarów w głównym sidebarze).
 *
 * Systemowy suwak zjadał szerokość wąskiego paska i wyglądał obco obok ikon. Kółko myszy i dotyk
 * dalej przewijają; strzałka jest aktywna tylko, gdy w danym kierunku jest co przewijać, a gdy
 * lista się mieści — strzałek nie ma wcale.
 *
 * Logika skopiowana z `RailScrollArea` w Sidebar.tsx, bo główny sidebar jest zamrożony (nie
 * wydzielamy z niego wspólnego kawałka). Różnica: krok przewinięcia to kilka pozycji, nie jedna
 * ikona — lista Ustawień ma kilkadziesiąt pozycji.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { IconButton } from '@/components/IconButton';

/** Krok strzałki ≈ trzy pozycje listy (36 px + odstęp). */
const SCROLL_STEP_PX = 120;

export function SettingsNavScrollArea({
  ariaLabel,
  children,
}: {
  ariaLabel: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLElement>(null);
  const [canUp, setCanUp] = useState(false);
  const [canDown, setCanDown] = useState(false);
  const overflows = canUp || canDown;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanUp(el.scrollTop > 1);
      setCanDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    // Zmiana wysokości okna, zwinięcie paska albo filtr wyszukiwarki zmienia to, czy lista się
    // mieści — obserwujemy i kontener, i jego treść.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(el);
    for (const child of Array.from(el.children)) ro?.observe(child);
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(update);
    mo?.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
      mo?.disconnect();
    };
  }, []);

  const scrollBy = (dir: 1 | -1) => {
    scrollRef.current?.scrollBy({ top: dir * SCROLL_STEP_PX, behavior: 'smooth' });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {overflows && (
        <IconButton
          size="sm"
          aria-label={t('tenant.settings.navScrollUp')}
          title={t('tenant.settings.navScrollUp')}
          onClick={() => scrollBy(-1)}
          disabled={!canUp}
          className="mb-1 shrink-0 self-center cursor-pointer disabled:opacity-30"
          data-testid="settings-nav-scroll-up"
        >
          <ChevronUp />
        </IconButton>
      )}
      <nav
        ref={scrollRef}
        className="min-h-0 flex-1 space-y-5 overflow-y-auto"
        aria-label={ariaLabel}
        data-scroll-arrows
      >
        {children}
      </nav>
      {overflows && (
        <IconButton
          size="sm"
          aria-label={t('tenant.settings.navScrollDown')}
          title={t('tenant.settings.navScrollDown')}
          onClick={() => scrollBy(1)}
          disabled={!canDown}
          className="mt-1 shrink-0 self-center cursor-pointer disabled:opacity-30"
          data-testid="settings-nav-scroll-down"
        >
          <ChevronDown />
        </IconButton>
      )}
    </div>
  );
}
